/**
 * Banner 管理 — GET/POST/PATCH/DELETE /api/admin/banners
 */
import { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface BannerRow {
  id: number;
  title: string;
  image_url: string;
  link: string | null;
  sort: number;
  is_published: boolean;
  create_time: string;
}

interface BannerForm {
  title: string;
  image_url: string;
  link?: string;
  sort: number;
  is_published: boolean;
}

export function BannerPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<BannerRow | null>(null);
  const [createForm] = Form.useForm<BannerForm>();
  const [editForm] = Form.useForm<BannerForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('platform.banner.create');
  const canEdit = hasNode('platform.banner.edit');
  const canDelete = hasNode('platform.banner.delete');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'banners'],
    queryFn: () => api.get<BannerRow[]>('/api/admin/banners'),
  });

  async function onCreate(values: BannerForm) {
    try {
      await api.post('/api/admin/banners', values);
      message.success('Banner 已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'banners'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: BannerForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/banners/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'banners'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onDelete(id: number) {
    try {
      await api.del(`/api/admin/banners/${id}`);
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['admin', 'banners'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '删除失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '封面',
      dataIndex: 'image_url',
      width: 120,
      render: (v: string) =>
        v ? <img src={v} alt="" style={{ height: 40, maxWidth: 100, objectFit: 'cover' }} /> : null,
    },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    {
      title: '跳转链接',
      dataIndex: 'link',
      width: 200,
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '状态',
      dataIndex: 'is_published',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">已发布</Tag> : <Tag>草稿</Tag>),
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 150,
      render: (_: unknown, row: BannerRow) => (
        <>
          {canEdit && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setEditRow(row);
                editForm.setFieldsValue({
                  title: row.title,
                  image_url: row.image_url,
                  link: row.link ?? undefined,
                  sort: row.sort,
                  is_published: row.is_published,
                });
              }}
            >
              编辑
            </Button>
          )}
          {canDelete && (
            <Popconfirm title="确定删除？" onConfirm={() => onDelete(row.id)}>
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建 Banner
          </Button>
        )}
      </div>

      <Table<BannerRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        title="新建 Banner"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ sort: 0, is_published: true }}
          onFinish={onCreate}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="image_url" label="封面 URL" rules={[{ required: true, max: 1024 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="link" label="跳转链接"><Input /></Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="立即发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title="编辑 Banner"
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="image_url" label="封面 URL" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="link" label="跳转链接"><Input /></Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
